import { SVGAssets } from '@business/assets/vectors';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { getPermissions } from '@ports/common/authStateSlice';
import { useSelector } from 'react-redux';
import {
  setIsFromRecipesPage,
  setMemoryFilterOptions,
  setPhotoFilterOptions,
  setRecipeFilterOptions,
  setFolderFilterOptions,
  setMomentFilterOptions,
  setPeopleFilterOptions,
  setTrashFilterOptions
} from '@ports/common/sessionStateSlice';
import { useDispatch } from 'react-redux';
import { Button } from '../Button';

interface SidebarSubItem {
  label: string;
  path: string;
}

interface SidebarItem {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  path: string;
  selectedIcon?: React.ComponentType<{ className?: string }>;
  children?: SidebarSubItem[];
  permission?: string;
}

export const Sidebar = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isAnyPeopleRoute = location.pathname.startsWith('/people');
  const [openPeople, setOpenPeople] = useState<boolean>(isAnyPeopleRoute);
  const permissions = useSelector(getPermissions);
  useEffect(() => {
    if (isAnyPeopleRoute) {
      setOpenPeople(true);
    } else {
      setOpenPeople(false);
    }
  }, [location.pathname, isAnyPeopleRoute]);

  const defaultFilterOptions = {
    search: '',
    accessType: 'public',
    sortBy: '',
    sharedWith: '',
    people: '',
    things: '',
    locations: '',
    date: '',
    interactions: '',
    mediaType: '',
    favorites: false
  };

  const resetFilterOptionsForPath = () => {
    // Reset ALL filters on every sidebar click to ensure clean slate
    dispatch(setPhotoFilterOptions(defaultFilterOptions));
    dispatch(setMemoryFilterOptions(defaultFilterOptions));
    dispatch(setRecipeFilterOptions(defaultFilterOptions));
    dispatch(setPeopleFilterOptions(defaultFilterOptions));
    dispatch(setFolderFilterOptions(defaultFilterOptions));
    dispatch(setMomentFilterOptions(defaultFilterOptions));
    dispatch(setTrashFilterOptions(defaultFilterOptions));
  };

  const sidebarItems: SidebarItem[] = [
    {
      icon: SVGAssets.FavoritesIcon,
      label: t('favorites'),
      path: '/favorites',
      selectedIcon: SVGAssets.FavWhite,
      permission: 'favorites:view'
    },
    {
      icon: SVGAssets.PhotosIcon,
      label: t('photos'),
      path: '/photos',
      selectedIcon: SVGAssets.PhotoWhite,
      permission: 'photos:view'
    },
    {
      icon: SVGAssets.PeopleIcon,
      label: t('people'),
      path: '/people',
      selectedIcon: SVGAssets.PeopleWhite,
      permission: 'people:view',
      children: [
        {
          label: t('family'),
          path: '/people/family'
        },
        {
          label: t('friends'),
          path: '/people/friends'
        }
      ]
    },
    {
      icon: SVGAssets.MemoriesIcon,
      label: t('memories'),
      path: '/memories',
      permission: 'memories:view',
      selectedIcon: SVGAssets.MemoryWhite
    },
    {
      icon: SVGAssets.FamilyTreeIcon,
      label: t('familyTree'),
      selectedIcon: SVGAssets.TreeWhite,
      permission: 'familyTree:view',
      path: '/family-tree'
    },
    {
      icon: SVGAssets.RecipesIcon,
      label: t('recipes'),
      path: '/recipes',
      selectedIcon: SVGAssets.RecipieWhite,
      permission: 'recipes:view'
    },
    {
      icon: SVGAssets.Shares,
      label: t('sharing'),
      path: '/sharing',
      selectedIcon: SVGAssets.ShareWhite,
      permission: 'sharing:view'
    },
    {
      icon: SVGAssets.StorageIcon,
      label: t('storage'),
      path: '/storage',
      selectedIcon: SVGAssets.StorageWhite,
      permission: 'storage:view'
    },
    {
      icon: SVGAssets.TrashIcon,
      label: t('trash'),
      path: '/trash',
      selectedIcon: SVGAssets.TrashWhite,
      permission: 'trash:view'
    }
  ];

  // const isActive = (path: string) => location.pathname.startsWith(path);

  const isParentActive = (item: SidebarItem) => {
    if (!item.path) return false;
    // For items with children, only mark active if it's the exact path or a child path
    if (item.children) {
      return (
        location.pathname === item.path ||
        item.children.some(child => location.pathname.startsWith(child.path))
      );
    }
    return location.pathname.startsWith(item.path);
  };

  return (
    <aside className="w-64 bg-white border-r border-gray-200 flex-shrink-0 overflow-y-auto">
      <nav className="p-4 space-y-1">
        {sidebarItems.map((item, index) => {
          const active = isParentActive(item);
          const Icon =
            active && item.selectedIcon ? item.selectedIcon : item.icon;

          return (
            <div key={index}>
              {/* MAIN MENU */}
              <Button
                btnStyle="link"
                disabled={
                  item.permission
                    ? !permissions?.includes(item.permission)
                    : false
                }
                onClick={() => {
                  if (item.path === '#') return; // Disabled item
                  if (item.children) {
                    // Always navigate to the parent path
                    dispatch(setIsFromRecipesPage(false));
                    resetFilterOptionsForPath();
                    navigate(item.path);
                    setOpenPeople(!openPeople);
                  } else if (item.path) {
                    resetFilterOptionsForPath();
                    navigate(item.path);
                  }
                }}
                className={`
            w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors
            ${
              active
                ? 'bg-[#49712B] text-white font-medium'
                : (
                      item.permission
                        ? permissions?.includes(item.permission)
                        : true
                    )
                  ? 'text-[#505050] hover:bg-[#ebe5e5]'
                  : 'text-[#9CA3AF]'
            }
          `}>
                <Icon className="w-5 h-5" />
                <span className="text-sm font-semibold">{item.label}</span>
              </Button>

              {/* SUBMENU */}
              {item?.children &&
                openPeople &&
                (!item.permission ||
                  permissions?.includes(item.permission)) && (
                  <div className="ml-10 mt-2 space-y-3">
                    {item?.children.map((sub, idx) => {
                      const subActive = location?.pathname === sub?.path;

                      return (
                        <button
                          onClick={() => {
                            navigate(sub?.path);
                            resetFilterOptionsForPath();
                          }}
                          key={idx}
                          className={`
    w-full text-left
    flex items-center gap-3 px-4 py-2 text-xs ml-2 rounded-lg
    text-[#505050] font-semibold
    ${subActive ? 'bg-[#ebe5e5]' : 'hover:bg-[#ebe5e5]'}
  `}>
                          <span>{sub?.label}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
            </div>
          );
        })}
      </nav>
    </aside>
  );
};
